import {
  HotTakesStatementThumbnail,
  HotTakesSummaryThumbnail,
  HotTakesThumbnail,
} from "@/components/shared/entity-thumbnails";
import type { ChatRenderContext } from "@/components/shared/chat-message-types";
import { normalizeHotTakesShapes } from "@/lib/activities/hot-takes-shapes";
import { isHotTakesActivity, useActivity } from "@/lib/api/activities";
import { useConversationParticipants } from "@/lib/hooks/conversations/use-conversation-participants";
import type {
  HotTakeAnswer,
  HotTakesConfigStatement,
} from "@somethings-ui/core/hot-takes";
import { useMemo } from "react";

interface HotTakesInteractionThumbnailProps {
  entityId: string;
  index: number;
  context?: ChatRenderContext;
}

type Card =
  | { kind: "summary" }
  | { kind: "statement"; statement: HotTakesConfigStatement };

export function HotTakesInteractionThumbnail({
  entityId,
  index,
  context,
}: HotTakesInteractionThumbnailProps) {
  const { data: activity } = useActivity(entityId);
  const hotTakesActivity = isHotTakesActivity(activity) ? activity : undefined;
  const { mentor, teen } = useConversationParticipants(context);

  const { statements, responses } = useMemo(
    () =>
      normalizeHotTakesShapes(
        hotTakesActivity?.config,
        hotTakesActivity?.result,
      ),
    [hotTakesActivity?.config, hotTakesActivity?.result],
  );

  const teenAnswerById = useMemo(
    () =>
      new Map<string, HotTakeAnswer>(
        responses.map((r) => [r.statementId, r.teenAnswer]),
      ),
    [responses],
  );

  const cards = useMemo<Card[]>(
    () => [
      { kind: "summary" },
      ...statements.map((s) => ({ kind: "statement" as const, statement: s })),
    ],
    [statements],
  );

  const matchesCount =
    (activity?.result as { matchesCount?: number } | null)?.matchesCount ?? 0;

  const card = cards[index];

  if (card?.kind === "statement") {
    return (
      <HotTakesStatementThumbnail
        statement={card.statement}
        teenAnswer={teenAnswerById.get(card.statement.id) ?? null}
        mentorAvatarUrl={mentor.avatarUrl}
        teenAvatarUrl={teen.avatarUrl}
      />
    );
  }

  if (card?.kind === "summary" && statements.length > 0) {
    return (
      <HotTakesSummaryThumbnail
        statements={statements}
        teenAnswerById={teenAnswerById}
        matchesCount={matchesCount}
        teenName={teen.name ?? ""}
        mentorAvatarUrl={mentor.avatarUrl}
        teenAvatarUrl={teen.avatarUrl}
      />
    );
  }

  return (
    <HotTakesThumbnail
      mentorAvatarUrl={mentor.avatarUrl}
      teenAvatarUrl={teen.avatarUrl}
      teenName={teen.name}
      matchesCount={matchesCount}
    />
  );
}
